import { Component, Input } from '@angular/core';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CheckoutReducer } from '@pages/checkout-app/checkout-store/reducers';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Shop } from '@services/shops/shop.model';
import { signal } from '@angular/core';
import { ShopService } from '@app/services/shops/shop.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { combineLatest, map } from 'rxjs';
import { IExampleTemplateView } from '../../../models';
import { CheckoutExampleService } from './checkout-example.service';

const EXTENSION_TYPE_KEYS: string[] = Object.values(ExtensionComponentType);

@Component({
  selector: 'checkout-example',
  templateUrl: './checkout-example.component.html',
  styleUrls: ['./checkout-example.component.scss'],
  standalone: false,
})
export class CheckoutExampleComponent {
  @Input() shop: Shop;
  liveExampleTemplate$ = this.store.select(CheckoutReducer.selectLiveExampleTemplateView);
  activeExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  // inside an extension only that extension previews; the list previews all of them
  previewView$ = combineLatest([
    this.liveExampleTemplate$,
    this.activeExtension$,
    this.extensionsService.currentExtensionType$,
  ]).pipe(
    map(([data, activeExtension, currentExtensionType]) => ({
      data: this.scopeToCurrentExtension(data, currentExtensionType),
      activeExtension,
    })),
  );
  readonly ExtensionComponentType = ExtensionComponentType;
  readonly shippingMethodOptions = [{ selected: true }, { selected: false }, { selected: false }];
  mode = signal<'mobile' | 'desktop'>('desktop');

  constructor(
    private store: Store<fromCheckoutReducer.State>,
    public shopService: ShopService,
    private checkoutExampleService: CheckoutExampleService,
    private extensionsService: ExtensionsService,
  ) {}

  isCartControlActive(data: any, activeExtension: ICheckoutExtensions | null): boolean {
    if (data?.cartControl?.mode === 'new') {
      return true;
    }
    if (activeExtension?.type !== ExtensionComponentType.CART_CONTROL) {
      return false;
    }
    return Boolean(data?.cartControl?.mode);
  }

  isDiscountRejectionCouponActive(data: any, activeExtension: ICheckoutExtensions | null): boolean {
    if (data?.discountRejectionCoupon?.mode === 'new') {
      return true;
    }
    if (activeExtension?.type !== ExtensionComponentType.DISCOUNT_REJECTION_COUPON) {
      return false;
    }
    return Boolean(data?.discountRejectionCoupon?.mode);
  }

  isDynamicReviewsActive(data: any, activeExtension: ICheckoutExtensions | null): boolean {
    if (data?.dynamicReviews?.mode === 'new') {
      return true;
    }
    if (activeExtension?.type !== ExtensionComponentType.DYNAMIC_REVIEWS) {
      return false;
    }
    return Boolean(data?.dynamicReviews?.mode);
  }

  isProductUpgradeActive(data: any, activeExtension: ICheckoutExtensions | null): boolean {
    if (data?.productUpgrade?.mode === 'new') {
      return true;
    }
    if (activeExtension?.type !== ExtensionComponentType.PRODUCT_UPGRADE) {
      return false;
    }
    return Boolean(data?.productUpgrade?.mode);
  }

  // Takes over the whole preview only while creating/editing multi-shipment; otherwise it renders
  // inline (read-only) inside the normal checkout mock alongside the other enabled extensions.
  isMultiShipmentActive(data: any, activeExtension: ICheckoutExtensions | null): boolean {
    if (data?.multiShipment?.mode === 'new') {
      return true;
    }
    if (activeExtension?.type !== ExtensionComponentType.MULTI_SHIPMENT) {
      return false;
    }
    return Boolean(data?.multiShipment?.mode);
  }

  navigationOptions = [
    { label: 'Cart', active: true, disabled: false },
    { label: 'Information', active: true, disabled: false },
    { label: 'Shipping', active: false, disabled: false },
    { label: 'Payment', active: false, disabled: true },
  ];

  isExtensionSelectedAndInShippingMethods(data: any): boolean {
    if (data) {
      for (const extensionName in data) {
        const extension = data[extensionName];
        if (extension?.mode) return extension?.template?.placementMode === 'linked';
      }
    }

    return false;
  }

  // keeps non-extension keys like isModal, or the surrounding mock loses its shape
  private scopeToCurrentExtension(
    data: IExampleTemplateView,
    currentExtensionType: ExtensionComponentType | null,
  ): IExampleTemplateView {
    if (!data || !currentExtensionType || !data[currentExtensionType]) {
      return data;
    }

    const scopedData = Object.entries(data).reduce((scoped, [key, value]) => {
      if (!EXTENSION_TYPE_KEYS.includes(key) || key === currentExtensionType) {
        scoped[key] = value;
      }
      return scoped;
    }, {});

    return scopedData as IExampleTemplateView;
  }
}
